import { useEffect, useRef, useState, forwardRef, useImperativeHandle } from 'react';
import { v4 as uuidv4 } from 'uuid';
import db from '@services/firebase-service';
import { doc, onSnapshot, type Firestore } from 'firebase/firestore';

import { useStreamData } from '@lib/hooks/useStreamData';
import { logToCloudWatch } from '@services/logger.service';
import { handleEngagement } from '@services/interactions-service';
import RatingPollsChart from './RatingsPollsChart';

const RatingPollsResult = (props: any) => {
  const { analyticsData: { data, id, liveStreamId, setting, color, title, type } } = props;

  return (
    <div className='w-full h-full'>
      <div className='ratingPollWrapper w-full h-full' id={`ratingPollsAnalytics-${id}`} >
        <RatingPollsChart origin="RESULT" data={data} colors={color} settings={setting} />
      </div>
    </div>
  );
};

const RatingPolls = forwardRef(({
  streamId, currentRosResult, currentRosFeature, colorData, streamSettings, platformType,
  startInteraction, setCurrentFeatureId, interactionType, isImpromptu,
  setShowRosModel, activeInteractionRef, updateFSMeta, setShowNotification,
  isAnalytics = false, analyticsData = {}, setStartTime, showInstantCustomisationPopup,
  setTotalResponders
}: any, ref) => {
  const defaultRatingPollsOptions = Array.from({ length: currentRosFeature?.options?.length || 0 }, (_, i) => ({
    id: uuidv4(),
    body: ` ${i + 1}`,
    option_number: i + 1,
    count: 0,
    users: []
  }));

  const initialRating = {
    averageRating: '0.0',
    contribution: defaultRatingPollsOptions.map((option) => ({
      option_number: option.option_number,
      percentage: 0,
      count: 0,
      body: option.body,
      users: []
    }))
  };
  if (isAnalytics) return <RatingPollsResult analyticsData={analyticsData} />;
  const {
    firestoreUnsub,
    setFirestoreUnsub,
    setFeatureId
  } = useStreamData(s => ({
    firestoreUnsub: s.firestoreUnsub,
    setFirestoreUnsub: s.setFirestoreUnsub,
    setFeatureId: s.setFeatureId
  }));

  const { isResult } = interactionType;
  const [ratingPollsId, setRatingPollsId] = useState(null);
  const [rating, setRating] = useState(initialRating);
  const totalCommentsRef: any = useRef([]);
  const [, setFeatureIdUpdated] = useState(false);

  const setupRatingPolls = ({ id, current, prevData, noOfResponders }: any) => {
    totalCommentsRef.current = current;
    setRating(prevData);
    setRatingPollsId(id);
    setFeatureId(id);
    setCurrentFeatureId(id);
    setTotalResponders(noOfResponders);
  };

  const handleRatingPolls = async ({ interactionId = null, interactionResult = {} }) => {
    const response = await handleEngagement({ streamId, platformType, currentRosFeature, interactionId });

    if (response.status && response.entity) {
      const interactionDetails = {
        id: response.entity.engagementId,
        type: currentRosFeature.type,
        prevData: interactionResult,
        noOfResponders: response.entity.noOfResponders || 0,
      };
      setupRatingPolls(interactionDetails);
      updateFSMeta({ activeInteraction: interactionDetails });
    }
  };

  useEffect(() => {
    if (currentRosFeature.id) {
      setRating(currentRosResult.length && currentRosResult[0] ? currentRosResult[0]?.data : initialRating);
    }
  }, [currentRosFeature.id, currentRosResult]);

  useEffect(() => {
    if (activeInteractionRef.current && activeInteractionRef.current?.type === 'ratingPolls') {
      setupRatingPolls({
        id: activeInteractionRef.current.id,
        current: [],
        counter: 0,
        prevData: initialRating,
        noOfResponders: activeInteractionRef.current.noOfResponders || 0
      });
    }
  }, [activeInteractionRef.current]);

  const unsubscribeFromFirestore = () => {
    if (firestoreUnsub.unsub) {
      firestoreUnsub.unsub();
    }
  };

  const stopRatingPolls = async () => {
    unsubscribeFromFirestore();
    // const sessionId = localStorage.getItem('sessionId');
    // await stopEngagement(streamId, 'ratingPolls', { engagementId: ratingPollsId, platformType, sessionId });
  };

  const handleStartInteraction = () => {
    if (currentRosResult && currentRosResult.length > 0) {
      handleRatingPolls({ interactionId: currentRosResult[0].id, interactionResult: currentRosResult[0].data });
    } else {
      handleRatingPolls({ interactionId: null, interactionResult: initialRating });
    }
    setStartTime(Date.now());
  };

  const handleStopInteraction = () => {
    stopRatingPolls();
    setRatingPollsId(null);
  };

  const manageRatingPollsInteraction = () => {
    if (!isResult && startInteraction) {
      handleStartInteraction();
    } else if (ratingPollsId) {
      handleStopInteraction();
    } else if (currentRosResult && currentRosResult.length > 0) {
      setTotalResponders(currentRosResult[0].noOfResponders || 0);
    }
  };

  useEffect(() => {
    if (!currentRosFeature.id) return;

    setShowRosModel(false);
    manageRatingPollsInteraction();
  }, [currentRosFeature.id, startInteraction]);

  useEffect(() => {
    setFeatureIdUpdated(true);
    if (ratingPollsId) {
      const logData = {
        streamId,
        interactionType: 'ratingPolls',
        interactionId: ratingPollsId
      };
      setFirestoreUnsub({
        unSub: onSnapshot(doc(db, 'streams', streamId, 'ratingPolls', ratingPollsId), (document: any) => {
          if (document.exists()) {
            setFeatureIdUpdated(false);
            const { data: firestoreRating, noOfResponders } = document.data();
            if (firestoreRating) {
              setShowNotification(false);

              if (currentRosFeature?.options?.length && firestoreRating.contribution) {
                const existingFirebaseOptions = firestoreRating.contribution || [];
                const newOptionsLength = currentRosFeature.options.length;

                const mergedOptions = Array.from({ length: newOptionsLength }, (_, i) => {
                  const firebaseOption = existingFirebaseOptions.find((opt: any) => opt.option_number === i + 1);
                  return firebaseOption || {
                    option_number: i + 1,
                    percentage: 0,
                    count: 0,
                    body: ` ${i + 1}`,
                    users: []
                  };
                });

                setRating({
                  averageRating: firestoreRating?.averageRating || '0.0',
                  contribution: mergedOptions
                });
              } else {
                setRating({
                  averageRating: firestoreRating?.averageRating || '0.0',
                  contribution: firestoreRating?.contribution
                });
              }
              setTotalResponders(noOfResponders || 0);
            }
          }
        }, (error: any) => logToCloudWatch('Failed to subscribe to firestore', { ...logData, error }, 'ERROR'))
      });
    }
    return () => {
      unsubscribeFromFirestore();
    };
  }, [ratingPollsId, currentRosFeature?.options?.length]);

  useImperativeHandle(ref, () => ({
    async triggerEngagement({ interactionResult }: { interactionResult: any }) {
      if (interactionResult?.length > 0) {
        await handleRatingPolls({ interactionId: interactionResult[0].id, interactionResult: interactionResult[0].data });
      }
    }
  }));

  return (
    <div className='ratingPollWrapper w-full h-full'>
      <RatingPollsChart
        origin="LIVE_PRESENTATION"
        data={rating}
        colors={currentRosFeature.color}
        settings={currentRosFeature.settings || currentRosFeature.setting}
      />
    </div>
  );
});

export default RatingPolls;
